import usePopups from "@/stores/popupsStore"
import { faChevronLeft, faChevronRight, faEllipsisH } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { Deferred, defineComponent, inferProps, ModalTitle, type ReactiveComponent } from "vue-utils"
import BootstrapButton from "../form/BootstrapButton"
import IconButton from "../form/IconButton"
import SelectNumberForm from "./SelectNumberForm"

interface Props {
	page: number
	totalPages: number
	pageSize?: number
	maxButtons?: number
	setPage(page: number): void
}

const pagingStyles = css`
	display: inline-flex;
	justify-content: center;
	list-style: none;
	gap: 0.5rem;
	padding: 0;
	margin-bottom: 0;

	button {
		display: flex;
		align-items: center;
		justify-content: center;
		user-select: none;
		text-align: center;
		min-width: 2rem;
		height: 2rem;
		padding: 0 0.125rem;
		transition: none;

		&:disabled {
			cursor: not-allowed;
			pointer-events: all;
		}
	}
`

const Pagination: ReactiveComponent<Props> = (props) => {
	const popups = usePopups()

	const pageButtons = computed(() => {
		const listSize = props.maxButtons ?? 9
		const result: number[] = []
		for (let i = 0; i < props.totalPages * 2 && result.length < listSize; i++) {
			const page = i % 2 === 0 ? props.page + i / 2 : props.page - (i + 1) / 2
			if (page < 1 || page > props.totalPages) {
				continue
			}
			result.push(page)
		}
		result.sort((a, b) => a - b)
		if (result.length > 2 && result[0] !== 1) {
			result.shift()
			result.shift()
		}
		if (result.length > 2 && result[result.length - 1] !== props.totalPages) {
			result.pop()
			result.pop()
		}
		return result
	})

	function renderButton(page: number) {
		return (
			<BootstrapButton
				style={{ fontSize: "1.25rem" }}
				color={props.page === page ? "primary" : "light"}
				onClick={() => props.setPage(page)}
			>
				{page}
			</BootstrapButton>
		)
	}

	async function handleSelectPageClick() {
		const deferred = new Deferred<number | null>()
		popups.openPopup({
			width: "24rem",
			onClose: () => deferred.resolve(null),
			content: (popupProps) => (
				<>
					<ModalTitle title="Enter Page Number" />
					<hr />
					<SelectNumberForm
						existingValue={null}
						minimum={1}
						maximum={props.totalPages}
						placeholder="Enter page number..."
						cancel={() => {
							popupProps.close()
							deferred.resolve(null)
						}}
						select={(page) => {
							popupProps.close()
							deferred.resolve(page)
						}}
					/>
				</>
			),
		})

		const selectedPage = await deferred
		if (selectedPage && selectedPage >= 1 && selectedPage <= props.totalPages) {
			props.setPage(selectedPage)
		}
	}

	return () => (
		<ul class={pagingStyles}>
			<IconButton
				color="secondary"
				icon={faChevronLeft}
				onClick={() => props.setPage(props.page - 1)}
				disabled={props.page === 1}
			/>
			{props.totalPages > 1 && !pageButtons.value.includes(1) && (
				<>
					<li>{renderButton(1)}</li>
					<li>
						<IconButton
							title="Navigate to specific page"
							color="light"
							icon={faEllipsisH}
							onClick={() => void handleSelectPageClick()}
						/>
					</li>
				</>
			)}
			{pageButtons.value.map((p) => (
				<li key={p}>{renderButton(p)}</li>
			))}
			{props.totalPages > 1 && !pageButtons.value.includes(props.totalPages) && (
				<>
					<li>
						<IconButton
							title="Navigate to specific page"
							color="light"
							icon={faEllipsisH}
							onClick={() => void handleSelectPageClick()}
						/>
					</li>
					<li>{renderButton(props.totalPages)}</li>
				</>
			)}
			<IconButton
				color="secondary"
				icon={faChevronRight}
				onClick={() => props.setPage(props.page + 1)}
				disabled={props.page === props.totalPages}
			/>
		</ul>
	)
}

export default defineComponent(Pagination, inferProps<Props>())
